/**
 * Import Dependency
 */

/**
 * Import API
 */

import { CHARGINGPOINT_CATEGORY } from '@/../shared/valueholders/chargingpoint-statuses'
import { Bugfender } from '@bugfender/sdk'
import { checkStatus, returnJson, fetchCompressedData } from '@/helpers/api'
import Vue from 'vue'
import _ from 'lodash'

/**
 * Declare Variable
 */
const state = {
  active: false,
  selectedChargingPointRefId: null,

  /**
   * Phases, that are added here, are locked and need to be unlocked by holding or extra clicking
   */
  lockedPhases: [],

  /**
   * helper alternative locations
   */
  rejectedLocationUuid: null,
  isPlanningAlternativeLocation: false,
  // for all locations that have been prompted
  flaggedLocations: new Set(),

  /**
   * All lines that should be displayed on the map.
   *
   * Lines are configurable
   *
   * @param {array} fromCoordinates
   * @param {array} toPoints
   * @param {boolean} [dashedLine] - false
   */
  mapLines: [],

  /**
   * All charging points
   */
  chargingpoints: [],

  /**
   * There is a layer per charging point type
   */
  layers: null,

  /*
  * All comments (used for search and filters)
  */
  allComments: [],

  /*
  * Comments by chargingpoint uuid (used for validation count indicator)
  */
  comments: [],

  /*
  * All participation comments
  */
  participationComments: [],
}

const getters = {
  isActive: state => !!state.active,
  isChargingpointSelected: state => state.selectedChargingPointRefId !== null,
  getSelectedChargingpointRefId: state => state.selectedChargingPointRefId,
  getSelectedChargingpoint: state => state.chargingpoints.find(chargingpoint => chargingpoint.ref['@ref'].id === state.selectedChargingPointRefId),
  getSelectedChargingpointUuid: (state, getters) => getters['getSelectedChargingpoint']?.data.uuid,

  getChargingPoints: state => state.chargingpoints.filter(chargingpoint => !chargingpoint.data.deleted_at),
  getChargingPointById: state => ({ id }) => state.chargingpoints.find(chargingpoint => chargingpoint.data.properties.id === id),
  getChargingPointByRefId: state => ({ refId }) => state.chargingpoints.find(chargingpoint => chargingpoint.ref['@ref'].id === refId),
  getChargingPointByUuid: state => ({ uuid }) => state.chargingpoints.find(chargingpoint => chargingpoint.data.uuid === uuid),
  getLockedPhases: state => state.lockedPhases,
  getPlannedChargingPoints: state => state.chargingpoints.filter(chargingpoint => CHARGINGPOINT_CATEGORY.PLANNED.includes(chargingpoint.data.properties.status)),

  /**
   * Used for the chargingpoint list in PlanModeSearch (search/filter sidebar left) and ChargerPointLayer (map)
   *
   * @return {array} - chargingpoints
   */
  getFilteredChargingpoints: (_, getters, rootState, rootGetters) => {
    if (!rootGetters['filters/getActiveFilters']) {
      return getters['getChargingpoints']
    }
    return rootGetters['filters/getActiveFilters']
      .reduce(
        // run all filter functions on given chargingpoints
        (chargingpoints, filter) => filter.filterFn({ chargingpoints, filter }),
        // start with only the chargingpoints that are visible on the map
        getters['getChargingPoints']
          .filter(chargingpoint =>
            getters['getPlanmodeLayers']?.find(layer => layer.id === `chargingpoints-${chargingpoint.data.properties.status}`)?.visible),
      )
  },
  getChargingPointIndexByRefId: state => ({ refId }) => {
    return state.chargingpoints.findIndex(chargingpoint => chargingpoint.ref['@ref'].id === refId)
  },
  getSuccessors: (state, getters) => {
    if (! getters['getSelectedChargingpointUuid']) return []

    return getters['getSuccessorsByUuid']({ uuid: getters['getSelectedChargingpointUuid'] })
  },
  getSuccessorsByUuid: (state, getters) => ({ uuid }) => {
    const needle = getters['getChargingPointByUuid']({ uuid })
    if (! needle) return []

    return getters['getChargingPoints']
      .filter(chargingpoint => {
        const { predecessor } = chargingpoint.data.properties

        if (! predecessor) return false
        if (predecessor.uuid !== needle.data.uuid) return false

        return true
      })
  },

  /**
   * get all predecessors in path recursively
   */
  getPredecessors: (state, getters) => {
    if (! getters['getSelectedChargingpointUuid']) return []

    const predecessor = getters['getSelectedChargingpoint'].data.properties.predecessor
    if (! predecessor) return []

    return getters['getPredecessorsByUuid']({ uuid: predecessor.uuid })
  },
  getPredecessorsByUuid: (state, getters) => ({ uuid }) => {
    const predecessor = getters['getChargingPointByUuid']({ uuid })

    if (! predecessor) {
      return []
    }

    let more = []
    if (predecessor?.data.properties.predecessor) {
      more = getters['getPredecessorsByUuid']({ uuid: predecessor?.data.properties.predecessor.uuid })
    }

    return [predecessor].concat(more)
  },
  getAllAlternativeLocationsByUuid: (state, getters) => ({ uuid }) => {
    const successors = getters['getSuccessorsByUuid']({ uuid })

    if (! successors.length) return []

    const more = successors.flatMap(successor => getters['getAllAlternativeLocationsByUuid']({ uuid: successor.data.uuid }))

    return successors.concat(more)
  },

  getRejectedLocationUuid: (state) => state.rejectedLocationUuid,
  isPlanningAlternativeLocation: (state) => state.isPlanningAlternativeLocation,
  getFlaggedLocations: (state) => state.flaggedLocations,

  /**
   * compute the lines to all successors, starting at uuid (origin) recursively
   */
  getLinesToSuccessorsRecursiveByUuid: (state, getters) => ({ uuid }) => {
    const chargingpoint = getters['getChargingPointByUuid']({ uuid })
    const successors = getters['getSuccessorsByUuid']({ uuid })

    if (! successors.length) return []

    const more = successors.flatMap(successor => getters['getLinesToSuccessorsRecursiveByUuid']({ uuid: successor.data.uuid }))

    return [{
      fromCoordinates: chargingpoint.data.coordinates,
      toPoints: successors.map(point => point.data.coordinates),
      dashedLine: true,
    }].concat(more)
  },

  /**
   * get all lines that should be drawn to the map
   */
  getMapLines: state => state.mapLines,

  /**
   * Chargingpoint layer state
   */
  getPlanmodeLayers: state => state.layers,

  /**
   * Chargingpoint comments
   */
  getComments: state => state.comments,

  /**
   * All comments for filtering e.g.
   */
  getAllComments: state => state.allComments,

  getCommentIndexByRefId: state => ({ refId }) => state.comments.findIndex(comment => comment.ref['@ref'].id === refId),
  getCommentsByChargingpointUuid: (state) => ({ chargingpointUuid }) => state.comments.filter(comment => comment.data.chargingpointUuid === chargingpointUuid),
  getAllCommentsByCurrentUser: (state, getters, rootState, rootGetters) => state.allComments.filter(comment => comment.data.UserId === rootGetters['currentUser/getCurrentUserId']),
  hasUserAlreadyVoted: (state, getters) => ({ chargingpointUuid }) => getters['getAllCommentsByCurrentUser']
    .filter(comment => comment.data.chargingpointUuid === chargingpointUuid)
    .filter(comment => comment.data.deleted_at === undefined)
    .some(comment => !!comment.data.validation),
  getCommentsBySelectedChargingpoint: (state, getters) => state.comments.filter(comment => comment.data.chargingpointUuid === getters['getSelectedChargingpointUuid']),

  /**
   * Participation comments
   */
  getParticipationComments: state => state.participationComments,
  getParticipationCommentIndexByRefId: state => ({ refId }) => state.participationComments.findIndex(comment => comment.ref['@ref'].id === refId),
  getParticipationCommentsByChargingpointUuid: (state) => ({ chargingpointUuid }) => state.participationComments.filter(({ data }) => data.chargingpointUuid === chargingpointUuid),
  getParticipationCommentsBySelectedChargingpoint: (state, getters) => state.participationComments.filter(comment => comment.data.chargingpointUuid === getters['getSelectedChargingpointUuid']),

  /**
   * Possible duplicates
   */
  getPossibleDuplicatesOfSelectedChargingpoint: (state, getters) => {
    // Get all chargingpoints referenced by the selected chargingpoint
    const selectedChargingpoint = getters['getSelectedChargingpoint']
    const possibleDuplicateOf = selectedChargingpoint.data?.possibleDuplicates?.map(
      (uuid) => getters['getChargingPointByUuid']({ uuid }),
    ) ?? []

    // Get all chargingpoints that reference the selected chargingpoint
    const allChargingpoints = getters['getChargingPoints']
    const possibleDuplicatedBy = allChargingpoints.filter(
      (chargingpoint) => chargingpoint.data?.possibleDuplicates?.includes(selectedChargingpoint.data.uuid),
    )

    // Return all collected chargingpoints
    return possibleDuplicateOf.concat(possibleDuplicatedBy)
  },

}
const actions = {
  /**
   * Add or update a chargingpoint that was saved to the Data Storage after the initial load event
   *  This may be triggered due to an action by the current user, or by another user through a Pusher event
   */
  addOrUpdateChargingPoint({ commit, getters, rootGetters }, { chargingpoint }) {
    if (! chargingpoint) return

    // Ignore actions that relate to chargingpoints from a municipality that is no longer active
    if (rootGetters['access/getActiveMunicipality'] !== chargingpoint.data.code) return

    let refId = chargingpoint.ref['@ref'].id
    let index = getters.getChargingPointIndexByRefId({ refId })

    if (index !== -1) {
      commit('updateChargingpoint', { chargingpoint, index })
    } else {
      commit('addChargingpoint', { chargingpoint })
    }
  },
  mergeChargingpoints({ commit, dispatch }, { chargingpoints, result }) {
    dispatch('deselectChargingPoint')
    const toBeDeleted = chargingpoints.find(uuid => uuid !== result.data.uuid)
    commit('deleteChargingpoint', { chargingpointUuid: toBeDeleted })
    dispatch('addOrUpdateChargingPoint', { chargingpoint: result })
  },
  removePossibleDuplicates({ dispatch, getters }, { uuid1, uuid2 }) {
    const chargingpoint1 = getters['getChargingPointByUuid']({ uuid: uuid1 })
    chargingpoint1.data.possibleDuplicates = chargingpoint1.data.possibleDuplicates?.filter(uuid => uuid !== uuid2) ?? []
    dispatch('addOrUpdateChargingPoint', { chargingpoint: chargingpoint1 })

    const chargingpoint2 = getters['getChargingPointByUuid']({ uuid: uuid2 })
    chargingpoint2.data.possibleDuplicates = chargingpoint2.data.possibleDuplicates?.filter(uuid => uuid !== uuid1) ?? []
    dispatch('addOrUpdateChargingPoint', { chargingpoint: chargingpoint2 })
  },
  // eslint-disable-next-line no-unused-vars
  async fetchCommentsByChargingpointUuid({ _, dispatch, rootGetters }, { chargingpointUuid }) {
    const code = rootGetters['access/getActiveMunicipality']
    const token = await this.$auth.getTokenSilently()

    return await fetchCompressedData('/api/commentsload', {
      method: 'POST',
      headers: {
        authorization: 'Bearer ' + token,
      },
      body: JSON.stringify({ code, chargingpointUuid }),
    })
      .then(response => {
        if (! response.comments) {
          return
        }

        dispatch('addOrUpdateComments', ({ comments: response.comments }))
      })
      .catch((e) => {
        Bugfender.error('fetchCommentsByChargingpointUuid: ', e)

        Vue.notify({
          type: 'error',
          title: 'Er is iets misgegaan!',
          text: 'De opmerkingen konden niet worden geladen',
        })
      })
  },

  // eslint-disable-next-line no-unused-vars
  async fetchAllComments({ commit }, { code }) {
    const token = await this.$auth.getTokenSilently()

    return await fetchCompressedData('/api/commentsload', {
      method: 'POST',
      headers: {
        authorization: 'Bearer ' + token,
      },
      body: JSON.stringify({ code }),
    })
      .then(response => {
        if (!response.comments) {
          return
        }

        commit('setAllComments', ({ comments: response.comments }))
      })
      .catch((e) => {
        Bugfender.error(e)

        Vue.notify({
          type: 'error',
          title: 'Er is iets misgegaan!',
          text: 'De opmerkingen konden niet worden geladen',
        })
      })
  },

  addOrUpdateComments({ commit }, { comments }) {
    commit('updateCommentsAsNeeded', { comments })
  },

  addOrUpdateComment({ commit, getters }, { comment }) {
    let refId = comment.ref['@ref'].id
    // todo:: this is bad for performance... shouldn't we set it up in an object notation so that we don't have to search in an array but can directly access it via UUID?
    let index = getters.getCommentIndexByRefId({ refId })

    if (index !== -1) {
      commit('updateComment', { comment, index })
    } else {
      commit('addComment', { comment })
    }
  },
  deleteComment({ commit, getters }, { comment }) {
    let refId = comment.ref['@ref'].id
    let index = getters.getCommentIndexByRefId({ refId })
    commit('updateComment', { comment: comment, index })
  },
  async fetchParticipationCommentsByChargingpointUuid({ dispatch }, { code, chargingpointUuid }) {
    const token = await this.$auth.getTokenSilently()

    return await fetch('/api/participationcommentsload', {
      method: 'POST',
      headers: {
        authorization: 'Bearer ' + token,
      },
      body: JSON.stringify({ code, chargingpointUuid }),
    })
      .then(await checkStatus)
      .then(returnJson)
      .then(({ comments }) => {
        if (comments) {
          comments.forEach(comment => dispatch('addOrUpdateParticipationComment', ({ comment })))
        }
      })
      .catch((e) => {
        Bugfender.error('fetchParticipationCommentsByChargingpointUuid: ', e)

        Vue.notify({
          type: 'error',
          title: 'Er is iets misgegaan!',
          text: 'De participatie opmerkingen konden niet worden geladen',
        })
      })
  },
  async fetchAllParticipationComments({ dispatch }, { code }) {
    const token = await this.$auth.getTokenSilently()

    return await fetch('/api/participationcommentsload', {
      method: 'POST',
      headers: {
        authorization: 'Bearer ' + token,
      },
      body: JSON.stringify({ code }),
    })
      .then(await checkStatus)
      .then(returnJson)
      .then(({ comments }) => {
        if (comments) {
          comments.forEach(comment => dispatch('addOrUpdateParticipationComment', ({ comment })))
        }
      })
      .catch((e) => {
        Bugfender.error(e)

        Vue.notify({
          type: 'error',
          title: 'Er is iets misgegaan!',
          text: 'De opmerkingen konden niet worden geladen',
        })
      })
  },
  addOrUpdateParticipationComment({ commit, getters }, { comment }) {
    let refId = comment.ref['@ref'].id
    let index = getters.getParticipationCommentIndexByRefId({ refId })

    if (index !== -1) {
      commit('updateParticipationComment', { comment, index })
    } else {
      commit('addParticipationComment', { comment })
    }
  },

  /**
   * Update the status already with the optimistic response, this will be reverted if something goes wrong while updating
   */
  async updateCommentStatus({ dispatch, rootGetters }, { comment, status }) {
    const oldComment = JSON.parse(JSON.stringify(comment))

    comment.data.status = status
    dispatch('addOrUpdateParticipationComment', ({ comment, where: 'in action' }))

    fetch('/api/participationcommentsave', {
      method: 'POST',
      headers: {
        authorization: `Bearer ${await this.$auth.getTokenSilently()}`,
      },
      body: JSON.stringify({
        code: rootGetters['access/getActiveMunicipality'],
        refId: comment.ref['@ref'].id,
        status,
      }),
    })
      .then(await checkStatus)
      .then(returnJson)
      .catch(() => {
        dispatch('addOrUpdateParticipationComment', ({ comment: oldComment, where: 'recall action' }))

        Vue.notify({
          type: 'error',
          title: 'Niet gelukt!',
          text: 'Er ging iets mis tijdens het opslaan van de comment status.',
        })

        Bugfender.warn(`Could not update the status of comment ${comment.ref['@ref'].id}`)
      })
  },

  selectChargingPoint({ dispatch, commit, getters }, { refId }) {
    commit('setSelectedChargingPoint', { refId })
    dispatch('showLinesToAlternativeLocationsByUuid', ({ uuid: getters['getSelectedChargingpoint'].data.uuid }))
    dispatch('showLinesToPossibleDuplicates') // TODO: distinguish lines from alternative locations
  },
  deselectChargingPoint({ commit, dispatch }) {
    commit('setSelectedChargingPoint', { refId: null })
    dispatch('hideLinesToAlternativeLocationsByUuid')
    dispatch('hideLinesToPossibleDuplicates')
  },

  /*
  * LAYERS
  */
  updatePlanmodeLayer({ commit }, { value, layer }) {
    commit('updatePlanmodeLayerVisibility', { value, id: layer.id })
  },

  /**
   * COMMANDS
   */
  activatePlanmode({ commit }) {
    commit('setActiveState', { active: true })
  },
  planAlternativeLocationForUuid({ commit, dispatch }, { uuid }) {
    commit('setRejectedLocationUuid', { uuid })
    dispatch('showLinesToAlternativeLocationsByUuid', { uuid })
  },
  clearAlternativeLocationForUuid({ commit }) {
    commit('setRejectedLocationUuid', { uuid: null })
  },
  showAlternativeLocations({ commit }) {
    commit('setIsPlanningAlternativeLocation', { active: true })
  },
  hideAlternativeLocations({ commit }) {
    commit('setIsPlanningAlternativeLocation', { active: false })
  },
  showLinesToAlternativeLocationsByUuid({ commit, getters }, { uuid }) {
    if (!uuid) return

    const origin = getters['getPredecessorsByUuid']({ uuid }).slice().pop() ?? getters['getSelectedChargingpoint']
    const lines = getters['getLinesToSuccessorsRecursiveByUuid']({ uuid: origin.data.uuid })
    commit('setMapLinesById', { id: 'alternate-locations', lines })
  },
  hideLinesToAlternativeLocationsByUuid({ commit }) {
    commit('setMapLinesById', { id: 'alternate-locations', lines: [] })
  },
  showLinesToPossibleDuplicates({ commit, getters }) {
    const selectedChargingpoint = getters['getSelectedChargingpoint']
    const possibleDuplicates = getters['getPossibleDuplicatesOfSelectedChargingpoint']
    const lines = [{
      fromCoordinates: selectedChargingpoint.data.coordinates,
      toPoints: possibleDuplicates.map(point => point.data.coordinates),
      dashedLine: true,
    }]
    commit('setMapLinesById', { id: 'possible-duplicates', lines })
  },
  hideLinesToPossibleDuplicates({ commit }) {
    commit('setMapLinesById', { id: 'possible-duplicates', lines: [] })
  },
  addToFlaggedLocations({ commit }, { chargingpoints }) {
    chargingpoints.forEach(chargingpoint => commit('addFlaggedLocation', { uuid: chargingpoint.data.uuid }))
  },
}
const mutations = {
  setActiveState(state, { active }) {
    state.active = !!active
  },
  setIsPlanningAlternativeLocation(state, { active }) {
    state.isPlanningAlternativeLocation = active
  },
  setRejectedLocationUuid(state, { uuid }) {
    state.rejectedLocationUuid = uuid
  },
  setMapLinesById(state, { id, lines }) {
    state.mapLines = state.mapLines.filter(line => line.id !== id)

    lines.forEach(newLine => state.mapLines.push({
      id,
      ...newLine,
    }))
  },
  addFlaggedLocation(state, { uuid }) {
    state.flaggedLocations.add(uuid)
  },
  /**
   * Upon loading the Chargingpoints data layer, or switching municipalities, the chargingpoints are loaded / replaced
   */
  setChargingPoints(state, { chargingpoints }) {
    state.chargingpoints = chargingpoints
  },
  setSelectedChargingPoint(state, { refId }) {
    state.selectedChargingPointRefId = refId
  },
  addChargingpoint(state, { chargingpoint }) {
    state.chargingpoints.push(chargingpoint)
  },
  updateChargingpoint(state, { chargingpoint, index }) {
    state.chargingpoints.splice(index, 1, chargingpoint)
  },
  deleteChargingpoint(state, { chargingpointUuid }) {
    state.chargingpoints = state.chargingpoints.filter(
      (chargingpoint) => chargingpoint.data.uuid !== chargingpointUuid,
    )
  },

  addComment(state, { comment }) {
    state.comments.push(comment)
    setTimeout(() => {
      state.allComments.push(comment)
    })
  },
  updateComment(state, { comment, index }) {
    const refId = comment.ref['@ref'].id
    const allCommentsIndex = state.allComments.findIndex(com => com.ref['@ref'].id === refId)

    state.comments.splice(index, 1, comment)
    state.allComments.splice(allCommentsIndex, 1, comment)
  },
  updateCommentsAsNeeded(state, { comments }) {
    state.comments = comments

    // make a copy of allComments
    const oldComments = JSON.parse(JSON.stringify(state.allComments))

    // update allComments with new comments
    const newComments = comments.reduce((acc, comment) => {
      const index = acc.findIndex(c => c.ref['@ref'].id === comment.ref['@ref'].id)
      if (index === -1) {
        acc.push(comment)
      } else {
        acc.splice(index, 1, comment)
      }

      return acc
    }, state.allComments.slice())

    // only update if there are changes, for performance reasons
    if (! _.isEqual(oldComments, newComments)) {
      state.allComments = newComments
    }
  },
  setAllComments(state, { comments }) {
    state.allComments = comments
  },
  addParticipationComment(state, { comment }) {
    state.participationComments.push(comment)
  },
  updateParticipationComment(state, { comment, index }) {
    state.participationComments.splice(index, 1, comment)
  },

  /**
   * Set the layer details (e.g. which types of charging points are visible)
   */
  setPlanmodeLayers(state, { layers }) {
    state.layers = layers
  },
  updatePlanmodeLayerVisibility(state, { value, id }) {
    const index = state.layers.findIndex(layer => layer.id === id)

    // Don't update the layer if it could not be found
    if (index === -1) {
      return
    }

    state.layers[index].visible = value
    // trigger update
    state.layers = [...state.layers]
  },
}

/**
 * Export
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
